import { useEffect, useMemo, useState } from 'react';
import { Plus, Edit, Trash2, GripVertical, ArrowLeft, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Combobox } from '@/components/ui/combobox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useActivityTypeStore } from '@/store/activityTypeStore';
import { useAuthStore } from '@/store/authStore';
import { moduleService, Module } from '@/services/moduleService';
import { toast } from 'sonner';

/** Allineato a backend defaultActivitiesValidation: niente < > \\ ` / ? né caratteri di controllo. */
const ACTIVITY_TYPE_CODE_FORBIDDEN = /[\u0000-\u001F\u007F<>\\`/?]/g;

function sanitizeActivityTypeCodeInput(value: string): string {
  return value.replace(ACTIVITY_TYPE_CODE_FORBIDDEN, '').toUpperCase().trim();
}

export default function ActivityTypesPage() {
  const navigate = useNavigate();
  const isAdmin = useAuthStore((s) => s.user?.role === 'ADMIN');
  const {
    activityTypes,
    loading,
    error,
    fetchActivityTypes,
    createActivityType,
    updateActivityType,
    deleteActivityType,
    reorderActivityTypes,
  } = useActivityTypeStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingType, setEditingType] = useState<any>(null);
  const [allModules, setAllModules] = useState<Module[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    icon: '',
    color: '',
    isActive: true,
    order: 0,
    actionType: 'file' as string,
    moduleId: '' as string,
  });

  useEffect(() => {
    fetchActivityTypes(false);
    moduleService.getModules().then(setAllModules).catch(() => {});
  }, [fetchActivityTypes]);

  const filteredActivityTypes = useMemo(() => {
    let result = activityTypes;
    if (statusFilter === 'active') result = result.filter(t => t.isActive);
    else if (statusFilter === 'inactive') result = result.filter(t => !t.isActive);
    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase();
      result = result.filter(
        (t) =>
          t.name?.toLowerCase().includes(query) ||
          t.code?.toLowerCase().includes(query) ||
          t.description?.toLowerCase().includes(query)
      );
    }
    return result;
  }, [activityTypes, searchTerm, statusFilter]);

  const handleCreate = async () => {
    try {
      const dataToSend = {
        ...formData,
        actionType: (formData.actionType || 'file') as 'file' | 'form' | null,
        moduleId: formData.actionType === 'form' && formData.moduleId ? formData.moduleId : null,
      };
      await createActivityType(dataToSend);
      setShowCreateModal(false);
      setFormData({
        name: '',
        code: '',
        description: '',
        icon: '',
        color: '',
        isActive: true,
        order: activityTypes.length,
        actionType: 'file',
        moduleId: '',
      });
    } catch (error: any) {
      // Errore già gestito nello store
    }
  };

  const handleEdit = (type: any) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      code: sanitizeActivityTypeCodeInput(String(type.code ?? '')),
      description: type.description || '',
      icon: type.icon || '',
      color: type.color || '',
      isActive: type.isActive,
      order: type.order,
      actionType: type.actionType || 'file',
      moduleId: type.moduleId || '',
    });
    setShowCreateModal(true);
  };

  const handleUpdate = async () => {
    if (!editingType) return;
    try {
      const dataToSend = {
        ...formData,
        actionType: (formData.actionType || 'file') as 'file' | 'form' | null,
        moduleId: formData.actionType === 'form' && formData.moduleId ? formData.moduleId : null,
      };
      await updateActivityType(editingType.id, dataToSend);
      await fetchActivityTypes(false);
      setShowCreateModal(false);
      setEditingType(null);
      setFormData({
        name: '',
        code: '',
        description: '',
        icon: '',
        color: '',
        isActive: true,
        order: 0,
        actionType: 'file',
        moduleId: '',
      });
    } catch (error: any) {
      // Errore già gestito nello store
    }
  };

  const handleSoftDelete = async (id: string) => {
    if (!window.confirm('Disattivare questo tipo attività? Potrà essere riattivato in seguito.')) {
      return;
    }
    try {
      await deleteActivityType(id);
      await fetchActivityTypes(false);
    } catch {
      // Errore già gestito nello store
    }
  };

  const handlePermanentDelete = async (id: string) => {
    if (
      !window.confirm(
        'Eliminare DEFINITIVAMENTE questo tipo attività dal database?\n\n' +
          'L’operazione non è reversibile. I moduli collegati verranno scollegati. Le attività già create nelle pratiche restano con il codice storico.'
      )
    ) {
      return;
    }
    try {
      await deleteActivityType(id, { permanent: true });
      await fetchActivityTypes(false);
    } catch {
      // Errore già gestito nello store
    }
  };

  const handleReorder = async (newOrder: { id: string; order: number }[]) => {
    try {
      await reorderActivityTypes(newOrder);
    } catch (error: any) {
      // Errore già gestito nello store
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/activities')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Torna alle attività
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tipi Attività</h1>
            <p className="mt-1 text-sm text-gray-500">
              Gestisci i tipi di attività disponibili
            </p>
          </div>
        </div>
        <Button onClick={() => {
          setEditingType(null);
          setFormData({
            name: '',
            code: '',
            description: '',
            icon: '',
            color: '',
            isActive: true,
            order: activityTypes.length,
            actionType: '',
            moduleId: '',
          });
          setShowCreateModal(true);
        }}>
          <Plus className="h-4 w-4 mr-2" />
          Nuovo Tipo
        </Button>
      </div>

      {/* Lista Tipi Attività */}
      <Card>
        <CardHeader>
          <CardTitle>Tipi Attività</CardTitle>
        </CardHeader>
        <CardContent>
          {activityTypes.length > 0 && (
            <div className="flex gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Cerca per nome, codice o descrizione..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as 'all' | 'active' | 'inactive')}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="active">Attivi</SelectItem>
                  <SelectItem value="inactive">Disattivi</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {loading && activityTypes.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Caricamento tipi attività...</p>
            </div>
          ) : activityTypes.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">Nessun tipo attività trovato</p>
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Crea Primo Tipo
              </Button>
            </div>
          ) : filteredActivityTypes.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">Nessun risultato per la ricerca</p>
              <Button variant="outline" onClick={() => setSearchTerm('')}>
                Cancella ricerca
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredActivityTypes.map((type, index) => (
                <div
                  key={type.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-center space-x-4 flex-1">
                    <GripVertical className="h-5 w-5 text-gray-400 cursor-move" />
                    <span className="text-sm font-medium text-gray-500 w-8">#{index + 1}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{type.name}</h3>
                        {type.isActive ? (
                          <Badge variant="default">Attivo</Badge>
                        ) : (
                          <Badge variant="outline">Disattivo</Badge>
                        )}
                        {type.isDefault && (
                          <Badge variant="secondary">Predefinito</Badge>
                        )}
                      </div>
                      <div className="mt-1 text-sm text-gray-600">
                        <span className="font-medium">Codice:</span> {type.code}
                        {type.description && (
                          <>
                            <span className="mx-2">•</span>
                            <span>{type.description}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(type)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    {!type.isDefault &&
                      (isAdmin ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" type="button" aria-label="Azioni eliminazione">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleSoftDelete(type.id)}>
                              Disattiva (nasconde il tipo, recuperabile)
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-red-600 focus:text-red-600"
                              onClick={() => handlePermanentDelete(type.id)}
                            >
                              Elimina definitivamente dal database
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSoftDelete(type.id)}
                          aria-label="Disattiva tipo attività"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Creazione/Modifica */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingType ? 'Modifica Tipo Attività' : 'Nuovo Tipo Attività'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Nome *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Es: Chiamata"
                />
              </div>
              <div>
                <Label htmlFor="code">Codice *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) =>
                    setFormData({ ...formData, code: sanitizeActivityTypeCodeInput(e.target.value) })
                  }
                  placeholder="Es: CALL"
                  maxLength={50}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="description">Descrizione</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrizione del tipo attività"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="icon">Icona</Label>
                <Input
                  id="icon"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  placeholder="Es: 📞"
                />
              </div>
              <div>
                <Label htmlFor="color">Colore</Label>
                <Input
                  id="color"
                  type="color"
                  value={formData.color || '#3b82f6'}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="order">Ordine</Label>
                <Input
                  id="order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="h-4 w-4"
                />
                <Label htmlFor="isActive">Attivo</Label>
              </div>
            </div>
            <div>
              <Label>Tipo Azione</Label>
              <Select
                value={formData.actionType || '_none_'}
                onValueChange={(v) => setFormData({ ...formData, actionType: v === '_none_' ? '' : v, moduleId: v !== 'form' ? '' : formData.moduleId })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Nessuna azione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none_">Nessuna azione</SelectItem>
                  <SelectItem value="file">File (inserimento documenti)</SelectItem>
                  <SelectItem value="form">Modulo (compilazione template)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Azione disponibile dopo l'accettazione dell'attività
              </p>
            </div>
            {formData.actionType === 'form' && (
              <div>
                <Label>Modulo associato</Label>
                <Combobox
                  options={allModules.map(m => ({
                    value: m.id,
                    label: m.name,
                    description: m.nasRelativePath || m.originalName,
                  }))}
                  value={formData.moduleId}
                  onValueChange={(v) => setFormData({ ...formData, moduleId: v })}
                  placeholder="Seleziona modulo..."
                  searchPlaceholder="Cerca modulo..."
                  emptyMessage="Nessun modulo disponibile. Sincronizza prima dalla pagina Moduli."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Il modulo template verrà copiato nella cartella pratica al completamento
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateModal(false);
              setEditingType(null);
            }}>
              Annulla
            </Button>
            <Button onClick={editingType ? handleUpdate : handleCreate}>
              {editingType ? 'Salva Modifiche' : 'Crea Tipo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

